// Report It client-snapshot capture (v7 §9.4–9.6, MV-1077).
//
// Gathers the self-contained media/WebRTC state the backend feeds to the
// agentic analysis as evidence: the GameCast video element's state, a short
// window of inbound-rtp stats (incl. jitter + transport round-trip time), a
// canvas black-frame probe, and page/network context.
//
// Deliberately decoupled from the GameCast SDK: the caller passes the video
// element + an optional getStats() provider, so the core is pure and unit
// testable and degrades gracefully when WebRTC stats or canvas readback are
// unavailable (every field is optional and a tri-state status is emitted for
// the black-frame probe).

import {
  ReportItClientSnapshot,
  MediaElementSnapshot,
  AudioStateSnapshot,
  RtcStatsSample,
  BlackFrameSampleStatus,
} from './snapshotTypes';

// Downscaled probe grid — large enough to be representative, small enough to be
// cheap (a few thousand pixels, read once).
const PROBE_WIDTH = 64;
const PROBE_HEIGHT = 36;
// Per-channel 0–255 ceiling under which a pixel counts as "black". A small
// non-zero value tolerates compression noise on a genuinely black frame.
const NEAR_BLACK_THRESHOLD = 16;

type RawStat = Record<string, unknown> & {
  type?: string;
  kind?: string;
  mediaType?: string;
};

function num(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/** WebRTC reports durations in seconds; the snapshot stores ms. */
function toMs(seconds: unknown): number | undefined {
  const s = num(seconds);

  if (s === undefined) {
    return undefined;
  }

  return Math.round(s * 1000);
}

/**
 * Probe whether the video is rendering black pixels via a downscaled canvas
 * readback. Returns a tri-state status so the backend can tell "we sampled and
 * it was black" from "we couldn't read the pixels" (cross-origin taint) from
 * "this browser can't sample at all".
 */
export function probeBlackFrame(
  video: HTMLVideoElement | null | undefined,
  doc: Document = document,
): { status: BlackFrameSampleStatus; ratio?: number } {
  if (!video || video.videoWidth === 0 || video.videoHeight === 0) {
    return { status: 'video-not-present' };
  }

  let ctx: CanvasRenderingContext2D | null;

  try {
    const canvas = doc.createElement('canvas');
    canvas.width = PROBE_WIDTH;
    canvas.height = PROBE_HEIGHT;
    ctx = canvas.getContext('2d');
  } catch {
    return { status: 'not-supported' };
  }

  if (!ctx) {
    return { status: 'not-supported' };
  }

  let pixels: Uint8ClampedArray;

  try {
    ctx.drawImage(video, 0, 0, PROBE_WIDTH, PROBE_HEIGHT);
    pixels = ctx.getImageData(0, 0, PROBE_WIDTH, PROBE_HEIGHT).data;
  } catch {
    // SecurityError — the canvas is tainted by cross-origin video frames, so
    // pixel readback is forbidden. That is itself a signal (we can see the
    // element but not its pixels), distinct from "not supported".
    return { status: 'blocked' };
  }

  const totalPixels = pixels.length / 4;

  if (totalPixels === 0) {
    return { status: 'failed' };
  }

  let blackPixels = 0;

  for (let i = 0; i < pixels.length; i += 4) {
    if (
      pixels[i] <= NEAR_BLACK_THRESHOLD &&
      pixels[i + 1] <= NEAR_BLACK_THRESHOLD &&
      pixels[i + 2] <= NEAR_BLACK_THRESHOLD
    ) {
      blackPixels += 1;
    }
  }

  return { status: 'sampled', ratio: blackPixels / totalPixels };
}

/**
 * Read the GameCast video element's media state + CSS visibility + black-frame
 * probe. Pure given the element; returns a not-present snapshot when absent.
 */
export function captureMediaElementSnapshot(
  video: HTMLVideoElement | null | undefined,
  win: Window = window,
  doc: Document = document,
): MediaElementSnapshot {
  if (!video) {
    return {
      present: false,
      readyState: 0,
      paused: true,
      ended: false,
      muted: false,
      currentTime: 0,
      videoWidth: 0,
      videoHeight: 0,
      blackFrameSampleStatus: 'video-not-present',
    };
  }

  const probe = probeBlackFrame(video, doc);

  let cssDisplay: string | undefined;
  let cssVisibility: string | undefined;
  let cssOpacity: string | undefined;

  try {
    const style = win.getComputedStyle(video);
    cssDisplay = style.display;
    cssVisibility = style.visibility;
    cssOpacity = style.opacity;
  } catch {
    // getComputedStyle can throw on a fully detached element — leave undefined.
  }

  return {
    present: true,
    readyState: video.readyState,
    networkState: video.networkState,
    paused: video.paused,
    ended: video.ended,
    muted: video.muted,
    currentTime: video.currentTime,
    videoWidth: video.videoWidth,
    videoHeight: video.videoHeight,
    clientWidth: video.clientWidth,
    clientHeight: video.clientHeight,
    cssDisplay,
    cssVisibility,
    cssOpacity,
    blackFrameRatio: probe.ratio,
    blackFrameSampleStatus: probe.status,
  };
}

/** Read the audio element's state, when one is provided. */
export function captureAudioSnapshot(
  audio: HTMLMediaElement | null | undefined,
): AudioStateSnapshot | undefined {
  if (!audio) {
    return undefined;
  }

  return {
    present: true,
    paused: audio.paused,
    muted: audio.muted,
    volume: audio.volume,
  };
}

function findInboundRtp(
  report: RTCStatsReport,
  kind: 'video' | 'audio',
): RawStat | undefined {
  let found: RawStat | undefined;

  report.forEach((raw: unknown) => {
    const stat = raw as RawStat;

    if (
      stat.type === 'inbound-rtp' &&
      (stat.kind === kind || stat.mediaType === kind)
    ) {
      found = stat;
    }
  });

  return found;
}

/**
 * Find the active ICE candidate pair. Firefox exposes `selected`; Chrome marks
 * the active pair `nominated`. As a last resort we take any candidate pair that
 * actually carries an RTT reading, so a non-standard stats shape still yields a
 * round-trip number.
 */
function findSelectedCandidatePair(
  report: RTCStatsReport,
): RawStat | undefined {
  let selected: RawStat | undefined;
  let nominated: RawStat | undefined;
  let withRtt: RawStat | undefined;

  report.forEach((raw: unknown) => {
    const stat = raw as RawStat;

    if (stat.type !== 'candidate-pair') {
      return;
    }

    if (stat.selected === true && !selected) {
      selected = stat;
    }

    if (stat.nominated === true && !nominated) {
      nominated = stat;
    }

    if (num(stat.currentRoundTripTime) !== undefined && !withRtt) {
      withRtt = stat;
    }
  });

  return selected ?? nominated ?? withRtt;
}

/**
 * Transport round-trip time (ms) from the selected ICE candidate pair. This is
 * the network latency governing input responsiveness — when video decode is
 * healthy but controls feel laggy, this is the number that localises it to the
 * network/server path (MV-1077). Returns undefined when no pair reports an RTT.
 */
export function extractTransportRoundTripMs(
  report: RTCStatsReport,
): number | undefined {
  const pair = findSelectedCandidatePair(report);

  if (!pair) {
    return undefined;
  }

  return toMs(pair.currentRoundTripTime);
}

/** Extract the inbound-rtp video sample from an RTCStatsReport. */
export function extractRtcVideoSample(
  report: RTCStatsReport,
  atMs: number,
): RtcStatsSample | undefined {
  const stat = findInboundRtp(report, 'video');

  if (!stat) {
    return undefined;
  }

  return {
    atMs,
    framesDecoded: num(stat.framesDecoded),
    framesReceived: num(stat.framesReceived),
    framesDropped: num(stat.framesDropped),
    framesPerSecond: num(stat.framesPerSecond),
    frameWidth: num(stat.frameWidth),
    frameHeight: num(stat.frameHeight),
    bytesReceived: num(stat.bytesReceived),
    freezeCount: num(stat.freezeCount),
    jitterMs: toMs(stat.jitter),
    // Connection-level RTT read from the same report. Lives on the video sample
    // since GameCast streams over a single peer connection.
    roundTripTimeMs: extractTransportRoundTripMs(report),
  };
}

/** Extract the inbound-rtp audio sample from an RTCStatsReport. */
export function extractRtcAudioSample(
  report: RTCStatsReport,
  atMs: number,
): RtcStatsSample | undefined {
  const stat = findInboundRtp(report, 'audio');

  if (!stat) {
    return undefined;
  }

  return {
    atMs,
    bytesReceived: num(stat.bytesReceived),
    jitterMs: toMs(stat.jitter),
  };
}

export interface RtcSampleResult {
  video: RtcStatsSample[];
  audio: RtcStatsSample[];
}

export interface GatherRtcSamplesOptions {
  getStats?: () => Promise<RTCStatsReport>;
  sampleCount?: number;
  intervalMs?: number;
  now?: () => number;
  sleep?: (ms: number) => Promise<void>;
}

/**
 * Sample inbound-rtp stats `sampleCount` times, `intervalMs` apart, so the
 * analysis can tell "frames advancing" from "frames flatlined" and watch RTT /
 * jitter move across the window. A flatlined frame series with audio still
 * flowing is the canonical GameLift video-track stall; a healthy frame series
 * with a climbing RTT points instead at network/input latency.
 * `now`/`sleep` are injectable for deterministic tests.
 */
export async function gatherRtcSamples(
  opts: GatherRtcSamplesOptions,
): Promise<RtcSampleResult> {
  const video: RtcStatsSample[] = [];
  const audio: RtcStatsSample[] = [];

  const getStats = opts.getStats;

  if (!getStats) {
    return { video, audio };
  }

  const sampleCount = Math.max(1, opts.sampleCount ?? 2);
  const intervalMs = opts.intervalMs ?? 1000;
  const now = opts.now ?? ((): number => Date.now());
  const sleep =
    opts.sleep ?? ((ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms)));
  const startMs = now();

  for (let i = 0; i < sampleCount; i += 1) {
    if (i > 0) {
      await sleep(intervalMs);
    }

    let report: RTCStatsReport;

    try {
      report = await getStats();
    } catch {
      // A failed getStats mid-window just yields fewer samples — the playbook
      // tolerates a short series.
      continue;
    }

    const atMs = now() - startMs;
    const videoSample = extractRtcVideoSample(report, atMs);
    const audioSample = extractRtcAudioSample(report, atMs);

    if (videoSample) {
      video.push(videoSample);
    }

    if (audioSample) {
      audio.push(audioSample);
    }
  }

  return { video, audio };
}

export interface CaptureClientSnapshotInput {
  video: HTMLVideoElement | null | undefined;
  audio?: HTMLMediaElement | null;
  rtcVideo?: RtcStatsSample[];
  rtcAudio?: RtcStatsSample[];
  capturedAt?: string;
  win?: Window;
  doc?: Document;
  nav?: Navigator;
}

/**
 * Assemble the full client snapshot from already-gathered inputs (pure). Only
 * non-empty RTC series and a present audio element are attached, keeping the
 * payload small for the size-capped POST /reports body.
 */
export function captureClientSnapshot(
  input: CaptureClientSnapshotInput,
): ReportItClientSnapshot {
  const win = input.win ?? window;
  const doc = input.doc ?? document;
  const nav =
    input.nav ?? (typeof navigator !== 'undefined' ? navigator : undefined);

  const video = captureMediaElementSnapshot(input.video, win, doc);
  const audio = captureAudioSnapshot(input.audio);

  const rvfcSupported =
    !!input.video &&
    typeof (input.video as unknown as Record<string, unknown>)
      .requestVideoFrameCallback === 'function';

  const snapshot: ReportItClientSnapshot = {
    capturedAt: input.capturedAt ?? new Date().toISOString(),
    video,
    requestVideoFrameCallbackSupported: rvfcSupported,
  };

  if (audio) {
    snapshot.audio = audio;
  }

  if (input.rtcVideo && input.rtcVideo.length > 0) {
    snapshot.rtcVideo = input.rtcVideo;
  }

  if (input.rtcAudio && input.rtcAudio.length > 0) {
    snapshot.rtcAudio = input.rtcAudio;
  }

  try {
    snapshot.pageVisibility =
      doc.visibilityState === 'hidden' ? 'hidden' : 'visible';
  } catch {
    // visibilityState unavailable — omit.
  }

  if (nav && typeof nav.onLine === 'boolean') {
    snapshot.online = nav.onLine;
  }

  return snapshot;
}

export interface CaptureReportItSnapshotOptions {
  video: HTMLVideoElement | null | undefined;
  audio?: HTMLMediaElement | null;
  getStats?: () => Promise<RTCStatsReport>;
  sampleCount?: number;
  intervalMs?: number;
  win?: Window;
  doc?: Document;
  nav?: Navigator;
  now?: () => number;
  sleep?: (ms: number) => Promise<void>;
}

/**
 * Top-level capture: sample WebRTC stats over a short window, then read the
 * media element + page state. Safe to call with no getStats provider — the
 * snapshot simply carries no RTC series.
 */
export async function captureReportItSnapshot(
  opts: CaptureReportItSnapshotOptions,
): Promise<ReportItClientSnapshot> {
  const rtc = await gatherRtcSamples({
    getStats: opts.getStats,
    sampleCount: opts.sampleCount,
    intervalMs: opts.intervalMs,
    now: opts.now,
    sleep: opts.sleep,
  });

  return captureClientSnapshot({
    video: opts.video,
    audio: opts.audio,
    rtcVideo: rtc.video,
    rtcAudio: rtc.audio,
    win: opts.win,
    doc: opts.doc,
    nav: opts.nav,
  });
}
